import React, { useState, useEffect, useMemo } from "react";
import { useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { setpublisherGlobalData } from '../../redux/currentpublisher/publisherGlobalDataSlice.js';
import { makeInputs, makeDateString, checkValidity } from '../../util.js';
import CreatorBox from "./CreatorBox.js";
import NewCreatorBox from "./NewCreatorBox.js";
import ImageUpload from "../../components/ImageUpload.js";
import CloudImage from "../../components/CloudImage.js";
import CREATE_IMPRINT_MUTATION from "../../graphql/mutations/PublisherData/CreateImprint.js";
import CREATE_SERIES_MUTATION from "../../graphql/mutations/PublisherData/CreateSeries.js";

import {
  TextInputFieldInput,
  TextInputWithUnitInput,
  TextareaFieldInput,
  ArrayInputFieldInput,
  DateFieldInput,
  CurrencyInput,
  ForeignKeyDropDownInput
} from "../../inputs/index.js";

import {
  Pane,
  Paragraph,
  Tablist,
  Tab,
  Text,
  Strong,
  majorScale,
  minorScale,
  Button,
  Popover,
  Tooltip,
  HelpIcon,
  Dialog
} from 'evergreen-ui';

function ProductForm({ product, editing, onUpdate, currentCreators, stagedCreators, deletedCreators, onAttachCreator, onRemoveCreator, publisherGlobalData, editMode, canDelete, handleDelete, handleImageUpload, handleRemoveInteriorImage }) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const onixGlobalData = useSelector((state) => state.onixGlobalData.value);
  const dispatch = useDispatch();
  const [tabs] = useState(['PRODUCT DETAILS', 'MARKETING/IMAGES'])
  const [selectedIndex, setSelectedIndex] = useState(0)
  // const localPublisherGlobalData = useSelector((state) => state.currentpublisher.publisherGlobalData); // Commented out as localPublisherGlobalData seems unused, replaced by publisherGlobalData prop

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageSrc, setLightboxImageSrc] = useState('');

  const openLightbox = (imageSrc) => {
    setLightboxImageSrc(imageSrc);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxImageSrc('');
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "Not set";
    try {
      if (typeof date === 'string') {
        // Try to parse the string as a date
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) return date; // If not valid date, return original string
        return parsedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      }
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    } catch (err) {
      console.log("Error formatting date:", err);
      return date?.toString() || "Not set";
    }
  };

  // Parse dimension value from JSON if needed
  const parseJsonDimension = (dimValue) => {
    if (dimValue === null || dimValue === undefined || dimValue === '') return '';

    try {
      // Check if it's a string that likely contains JSON
      if (typeof dimValue === 'string' && (dimValue.includes('{') || dimValue.includes('\\'))) {
        let jsonString = dimValue;

        // Step 1: Handle strings that are JSON representations of *other strings* containing JSON.
        // Example: "\"{\\\"value\\\": 10.0, \\\"unit\\\": \\\"lbs\\\"}\""
        if (jsonString.startsWith('"') && jsonString.endsWith('"') && jsonString.includes('\\"value\\')) {
          try {
            jsonString = JSON.parse(jsonString); // Parse the outer string to get the inner string
          } catch (e) {
            console.error("Error parsing outer JSON string layer:", e, dimValue);
            // If outer parse fails, maybe it wasn't double-encoded? Fall through.
          }
        }

        // Step 2: Parse the resulting string (which should now be like '{"value":...}')
        // Check if it's *still* a string and looks like a JSON object
        if (typeof jsonString === 'string' && jsonString.startsWith('{') && jsonString.endsWith('}')) {
          const parsed = JSON.parse(jsonString);
          return parsed.value !== undefined ? String(parsed.value) : ''; // Return value as string
        }
        // If after the first parse (or if it wasn't needed), we already have the object
        else if (typeof jsonString === 'object' && jsonString !== null && jsonString.value !== undefined) {
          return String(jsonString.value);
        }
        // If it's a string but doesn't look like JSON object after potential first parse
        else if (typeof jsonString === 'string') {
           return jsonString; // Treat as a simple string value
        }
      }

      // Handle direct numeric values
      if (typeof dimValue === 'number') {
        return String(dimValue);
      }

      // Handle cases where it might *already* be the object { value: ..., unit: ... }
      if (typeof dimValue === 'object' && dimValue !== null && dimValue.value !== undefined) {
        return String(dimValue.value);
      }

      // If it's a string but doesn't contain JSON indicators, return directly
      if (typeof dimValue === 'string') {
          return dimValue;
      }

      // Fallback for completely unexpected formats
      console.warn("Unexpected dimension format during parsing:", dimValue);
      return '';

    } catch (err) {
      console.error(`Error processing dimension: ${err.message}`, dimValue);
      // Return the original value (as string) if parsing fails completely
      return (typeof dimValue === 'string' || typeof dimValue === 'number') ? String(dimValue) : '';
    }
  };

  // Process the product data to handle various formats
  const processedProduct = useMemo(() => {
    if (!product) return {};

    // Make a copy of the product to avoid modifying original
    const copy = { ...product };

    // Parse dates properly
    if (copy.shipDate) {
      try {
        // If it's a string, ensure it's a valid date format
        if (typeof copy.shipDate === 'string') {
          const parsedDate = new Date(copy.shipDate);
          if (!isNaN(parsedDate.getTime())) {
            copy.shipDate = parsedDate;
          }
        }
      } catch (e) {
        console.log("Error parsing shipDate:", e);
      }
    }

    if (copy.publicationDate) {
      try {
        // If it's a string, ensure it's a valid date format
        if (typeof copy.publicationDate === 'string') {
          const parsedDate = new Date(copy.publicationDate);
          if (!isNaN(parsedDate.getTime())) {
            copy.publicationDate = parsedDate;
          }
        }
      } catch (e) {
        console.log("Error parsing publicationDate:", e);
      }
    }

    // Parse dimensions
    copy.parsedWeight = {
      value: parseJsonDimension(copy.weight),
      unit: "lbs"
    };
    copy.parsedWidth = {
      value: parseJsonDimension(copy.width),
      unit: "in"
    };
    copy.parsedHeight = {
      value: parseJsonDimension(copy.height),
      unit: "in"
    };
    copy.parsedThickness = {
      value: parseJsonDimension(copy.thickness),
      unit: "in"
    };

    let parsedInteriorImages = [];
    if (copy.interiorImages && typeof copy.interiorImages === 'string') {
      try {
        const parsed = JSON.parse(copy.interiorImages);
        if (Array.isArray(parsed)) {
          parsedInteriorImages = parsed;
        } else {
          // Attempt to parse again if it might be double-stringified
          const doubleParsed = JSON.parse(parsed);
          if (Array.isArray(doubleParsed)) {
            parsedInteriorImages = doubleParsed;
          }
        }
      } catch (e) {
        console.error("Error parsing interiorImages JSON in ProductForm:", e, "Value was:", copy.interiorImages);
        // If it's not a valid JSON string that results in an array, treat as empty or handle error
        // For now, parsedInteriorImages remains []
      }
    } else if (Array.isArray(copy.interiorImages)) {
      // This case should ideally not happen if backend always sends a string
      parsedInteriorImages = copy.interiorImages;
    }
    copy.parsedInteriorImages = parsedInteriorImages; // Add to the copy for rendering

    return copy;
  }, [product]);

  if (!processedProduct) {
    return null;
  }

  // Define format-specific inputs ONLY
  const formatSpecificInputs = {
    status: {
      width: 120,
      comp: ForeignKeyDropDownInput,
      formLabel: "STATUS:",
      handleChange: onUpdate,
      group: 10,
      values: onixGlobalData.onixproductavailabilitys,
      opts: {
        badge: true,
        marginRight: 16,
        short: true,
        preserveObject: true,
        valueKey: 'id',
        labelKey: 'description'
      }
    },
    subtitle: { width: 350, comp: TextInputFieldInput, formLabel: "Subtitle", handleChange: onUpdate, group: 1, opts: { marginRight: 16 } },
    volume: { width: 75, comp: TextInputFieldInput, formLabel: "Volume", handleChange: onUpdate, group: 11, opts: { marginRight: 16, marginBottom: 16 } },
    edition: { width: 75, comp: TextInputFieldInput, formLabel: "Edition", handleChange: onUpdate, group: 11, opts: { marginRight: 16, marginBottom: 16 } },
    isbn: { width: 200, comp: TextInputFieldInput, formLabel: "ISBN/UID", handleChange: onUpdate, group: 2, opts: { marginRight: 16 } },
    retailPrice: { width: 75, comp: CurrencyInput, formLabel: "Retail Price", handleChange: onUpdate, group: 3, opts: { mandatory: true, marginRight: 16 } },
    inventoryQuantity: { width: 75, comp: TextInputFieldInput, formLabel: "Inventory", handleChange: onUpdate, group: 3, opts: { marginRight: 16, marginBottom: 16 } },
    cartonQty: { width: 75, comp: TextInputFieldInput, formLabel: "Carton Qty", handleChange: onUpdate, group: 3, opts: { marginRight: 16 } },
    formatDetail: { width: 120, comp: ForeignKeyDropDownInput, formLabel: "Format Detail", handleChange: onUpdate, group: 41, values: onixGlobalData.onixproductformdetails, opts: { marginRight: 16, short:true } },
    format: {
      width: 120,
      comp: ForeignKeyDropDownInput,
      formLabel: "Format",
      handleChange: onUpdate,
      group: 4,
      values: onixGlobalData.onixproductforms,
      opts: {
        mandatory: true,
        marginRight: 16,
        short: true,
        preserveObject: true,
        valueKey: 'id',
        labelKey: 'description'
      }
    },
    shipDate: { width: 75, comp: DateFieldInput, formLabel: "Ship Date", handleChange: onUpdate, group: 5, opts: { marginRight: 16, tooltip: "The date this title is released from the warehouse" } },
    publicationDate: { width: 75, comp: DateFieldInput, formLabel: "Publication Date", handleChange: onUpdate, group: 5, opts: { marginRight: 16, tooltip: "The date this title is on sale in stores" } },
    parsedWeight: {
      width: 75,
      comp: TextInputWithUnitInput,
      formLabel: "Weight",
      handleChange: (obj) => {
        // Convert to regular weight format for backend
        onUpdate({ key: 'weight', val: obj.val });
      },
      group: 6,
      opts: { marginRight: 16 }
    },
    parsedWidth: {
      width: 75,
      comp: TextInputWithUnitInput,
      formLabel: "Width",
      handleChange: (obj) => {
        // Convert to regular width format for backend
        onUpdate({ key: 'width', val: obj.val });
      },
      group: 6,
      opts: { marginRight: 16 }
    },
    parsedHeight: {
      width: 75,
      comp: TextInputWithUnitInput,
      formLabel: "Height",
      handleChange: (obj) => {
        // Convert to regular height format for backend
        onUpdate({ key: 'height', val: obj.val });
      },
      group: 6,
      opts: { marginRight: 16 }
    },
    parsedThickness: {
      width: 75,
      comp: TextInputWithUnitInput,
      formLabel: "Thickness",
      handleChange: (obj) => {
        // Convert to regular thickness format for backend
        onUpdate({ key: 'thickness', val: obj.val });
      },
      group: 61,
      opts: { marginRight: 16 }
    },
    pageCount: { width: 100, comp: TextInputFieldInput, formLabel: "Page Count", handleChange: onUpdate, group: 61, opts: { marginRight: 16, marginBottom: 24 } },
    adminNotes: { width: 400, comp: TextareaFieldInput, formLabel: "Admin Notes(Only Visible Internally)", handleChange: onUpdate, group: 7, opts: { marginRight: 16 } },
    audience: { width: 120, comp: ForeignKeyDropDownInput, formLabel: "Audience", handleChange: onUpdate, group: 81, values: onixGlobalData.onixaudiencetypes, opts: { marginRight: 16 } },
    audienceDetails: { width: 200, comp: TextInputFieldInput, formLabel: "Audience Details", handleChange: onUpdate, group: 81, opts: { marginRight: 16 } },
    keySellingPoints: { width: 400, comp: TextareaFieldInput, formLabel: "Key Selling Points", handleChange: onUpdate, group: 8, opts: { marginRight: 16 } },
    marketing: { width: 400, comp: TextareaFieldInput, formLabel: "Marketing Notes", handleChange: onUpdate, group: 8, opts: { marginRight: 16 } },
    bisac: { width: 75, comp: ArrayInputFieldInput, formLabel: "BISAC", handleChange: onUpdate, group: 82, max: 4 },
  };

  // Pass the format-specific inputs object to makeInputs
  const inputs = { ...formatSpecificInputs };
  const formInputs = makeInputs(processedProduct, inputs, editing);

  // Helper to safely display dimension values
  const displayDimension = (dimension) => {
    if (!dimension) return "0";
    const value = dimension.value !== undefined ? dimension.value : "";
    const unit = dimension.unit || "";
    // Convert everything to strings and ensure we don't return objects
    return typeof value === 'object' ? "0" : `${value || "0"} ${unit}`.trim();
  };

  return (
    <Pane >
      <Tablist>
        {tabs.map((tab, index) => (
          <Tab
            aria-controls={`panel-${tab}`}
            isSelected={index === selectedIndex}
            key={tab}
            onSelect={() => setSelectedIndex(index)}
            className="custom-tab"
            fontWeight="bold"
          >
            {tab}
          </Tab>
        ))}
      </Tablist>
      <Pane>
        <Pane id="item-details" elevation={1}
          aria-labelledby={'PRODUCT DETAILS'}
          aria-hidden={0 !== selectedIndex}
          display={0 === selectedIndex ? 'flex' : 'none'}
          key={'PRODUCT DETAILS'}
          role="tabpanel"
          className="tab-content"
        >
          <Pane display="flex" flexDirection="column" borderRight="default" paddingRight={16} marginRight={16} flexBasis="33%">
            <Pane marginBottom={majorScale(2)}>{formInputs.group10}</Pane>
            <Pane display="flex" flexDirection="row" alignItems="flex-start" marginBottom={majorScale(2)}>
               {formInputs.group4}
               {formInputs.group41}
             </Pane>
             <Pane marginBottom={majorScale(2)}>{formInputs.group2}</Pane>
             <Pane display="flex" flexDirection="row" marginBottom={majorScale(2)}>
                {formInputs.group5}
             </Pane>
             <Pane marginTop={majorScale(4)}>
               <Popover
                 content={({ close }) => (
                   <Pane padding={16} >
                     <Paragraph>Are you sure you want to delete this format ({product.format?.description} - {product.isbn})?</Paragraph>
                     <Paragraph>This action cannot be undone.</Paragraph>
                     <Pane marginTop={16}>
                       <Button
                         marginRight={16}
                         appearance="primary"
                         intent="danger"
                         onClick={() => {
                           handleDelete();
                           close();
                         }}
                         fontWeight="bold"
                         className="btn-save-cancel-txt-bold"
                       >
                         DELETE
                       </Button>
                       <Button onClick={close}>Cancel</Button>
                     </Pane>
                   </Pane>
                 )}
                 shouldCloseOnExternalClick={false}
               >
                 <Button
                   intent="danger"
                   disabled={!canDelete || editing}
                   appearance="minimal"
                 >
                   Delete this format
                 </Button>
               </Popover>
               {(!canDelete && !editing) &&
                 <Tooltip content="This format cannot be deleted because it has associated orders or expenses.">
                   <HelpIcon color="muted" marginLeft={majorScale(1)} />
                 </Tooltip>
               }
               {(editing && !canDelete) &&
                 <Tooltip content="Cannot delete while editing. Also, this format has associated orders or expenses.">
                   <HelpIcon color="muted" marginLeft={majorScale(1)} />
                 </Tooltip>
               }
                {(editing && canDelete) &&
                 <Tooltip content="Save or cancel edits before deleting.">
                   <HelpIcon color="muted" marginLeft={majorScale(1)} />
                 </Tooltip>
               }
             </Pane>
          </Pane>
          <Pane display="flex" flexDirection="column" borderRight="default" paddingX={16} marginRight={16} flexBasis="33%">
            {formInputs.group1}
             <Pane display="flex">
                {formInputs.group11}
             </Pane>
             <Pane display="flex" flexDirection="row">
                {formInputs.group3}
             </Pane>
             <Pane display="flex" flexDirection="row">
                {formInputs.group6}
             </Pane>
             <Pane display="flex" flexDirection="row">
                {formInputs.group61}
             </Pane>


            {formInputs.group7}
          </Pane>
          <Pane paddingLeft={16} display="flex" flexDirection="column" flexBasis="33%">
             <Pane display={editMode === "new" ? 'block' : 'none'}>
              <NewCreatorBox
                editing={editing}
                onixGlobalData={onixGlobalData}
                royaltySet={[]}
                productcreatorSet={currentCreators}
                handleUpdateCreatorStaged={onAttachCreator}
                creators={publisherGlobalData?.publishercreators || []}
                handleDelete={onRemoveCreator}
                productId={null}
                editMode={editMode}
              />
            </Pane>
            <Pane display={editMode !== "new" ? 'block' : 'none'}>
              <CreatorBox
                editing={editing}
                onixGlobalData={onixGlobalData}
                productcreatorSet={currentCreators}
                handleUpdateCreatorStaged={onAttachCreator}
                handleDelete={onRemoveCreator}
                dataSourceForDropdown={publisherGlobalData?.publishercreators || []}
                productId={product?.id}
                editMode={editMode}
              />
            </Pane>
          </Pane>
        </Pane>
        <Pane id="marketing" elevation={1}
          aria-labelledby={'MARKETING/IMAGES'}
          aria-hidden={1 !== selectedIndex}
          display={1 === selectedIndex ? 'flex' : 'none'}
          key={'MARKETING/IMAGES'}
          role="tabpanel"
          className="tab-content"
        >
          <Pane paddingRight={16} borderRight="default" flex={1}>
             {editMode !== "new" &&
                <Pane>
                  {/* <Paragraph size={300} color="#667b7f" fontWeight="bold">COVER IMAGE</Paragraph> */}
                  <Pane marginTop={majorScale(2)}>
                    <Text display="block" marginBottom={minorScale(1)}><Strong>Cover Image</Strong></Text>
                    {editing && (
                      <ImageUpload
                        el="coverImage" // el is used by ProductForm's makeInputs or similar, not directly by ImageUpload.js for path
                        // currentImage={processedProduct.coverImage} // ImageUpload.js doesn't use this to display the image
                        handleImageUpload={(uploadedUrl) => handleImageUpload('coverImage', uploadedUrl)}
                        itemId={product.id ? String(product.id) : undefined} // Correct prop name for ImageUpload.js
                        pathType="products/cover" // Correct prop name for ImageUpload.js
                        disabled={!product.id} // Removed !editing from here as it's in parent conditional
                      />
                    )}
                    {/* Live preview for cover image - shown when editing and image exists, or always if not editing */}
                    {(processedProduct.coverImage) && (
                      <Pane marginTop={editing ? majorScale(1) : 0} cursor="pointer" onClick={() => openLightbox(processedProduct.coverImage)}>
                        <CloudImage
                          src={processedProduct.coverImage}
                          size={150}
                          altText="Cover Image Preview"
                          displayAs="thumbnail"
                        />
                      </Pane>
                    )}
                  </Pane>
                </Pane>
            }
          </Pane>
          <Pane display="flex" flexDirection="column" paddingX={16} borderRight="default" flex={2}>
            <Pane display="flex" justifyContent="flex-start" alignItems="flex-start">
               {formInputs.group81}
            </Pane>
            {formInputs.group82}
            {formInputs.group8}
          </Pane>
          <Pane paddingLeft={16} flex={2}>
            {editMode !== "new" && (
              <Paragraph size={300} color="#667b7f">Interior Images</Paragraph>
            )}
            {editMode !== "new" && editing && (
              <Pane>
                <Text display="block" marginBottom={minorScale(1)}><Strong>Interior Images</Strong></Text>
                {
                  Array.isArray(processedProduct.parsedInteriorImages) && processedProduct.parsedInteriorImages.map((imagePath, index) => (
                    <Pane key={index} display="flex" alignItems="center" marginBottom={minorScale(1)}>
                      <Pane cursor="pointer" onClick={() => openLightbox(imagePath)} marginRight={majorScale(1)}>
                        <CloudImage
                          src={imagePath}
                          size={100}
                          altText={`Interior Image ${index + 1}`}
                          displayAs="thumbnail"
                        />
                      </Pane>
                      {editing && (
                        <Button
                          onClick={() => handleRemoveInteriorImage(imagePath)}
                          intent="danger"
                          iconBefore="cross"
                        >
                          Remove
                        </Button>
                      )}
                    </Pane>
                  ))
                }
                <ImageUpload
                  el="interiorImages"
                  handleImageUpload={(uploadedUrl) => handleImageUpload('interiorImages', uploadedUrl)}
                  productId={product.id}
                  imageType="products/interior"
                  disabled={!editing}
                  isMulti={true}
                  buttonText="Upload Interior Image(s)"
                />
              </Pane>
            )}
            {editMode !== "new" && !editing && (
              <Pane display="flex" flexWrap="wrap" maxHeight={600} overflow="auto" border="default" padding={8}>
                {(() => {
                  if (!processedProduct.parsedInteriorImages || processedProduct.parsedInteriorImages.length === 0) {
                    return <Paragraph>No interior images.</Paragraph>;
                  }
                  return processedProduct.parsedInteriorImages.map((path, index) => (
                    <Pane key={index} cursor="pointer" onClick={() => openLightbox(path)} style={{ margin: '4px' }}>
                      <CloudImage
                        src={path}
                        customClass="thumbnail"
                        size={200}
                        name={`Interior Image ${index + 1}`}
                        displayAs="thumbnail"
                        // style prop removed as it was on Pane now
                      />
                    </Pane>
                  ));
                })()}
              </Pane>
            )}
          </Pane>
        </Pane>
      </Pane>
      <Dialog
        isShown={isLightboxOpen}
        onCloseComplete={closeLightbox}
        hasHeader={false}
        hasFooter={false}
        width="80vw"
        maxWidth="1000px"
        minHeight="600px"
        height="auto"
        contentContainerProps={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: majorScale(2),
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // Semi-transparent background
          minHeight: '600px',
          height: 'auto'
        }}
        preventBodyScrolling
      >
        {lightboxImageSrc && (
          <Pane
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="600px"
          >
            <CloudImage
              src={lightboxImageSrc}
              altText="Lightbox Image"
              // Ensure image displays at a consistent size while maintaining aspect ratio
              size={800} // Base size for the component
              style={{
                minWidth: '600px',
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              displayAs="original"
            />
          </Pane>
        )}
      </Dialog>
    </Pane>
  )
}

export default ProductForm;
